import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahfv6boh653ivfvntr0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交内容未通过校验，请调整后再试一次。'
            : typeof serverMsg === 'string' && serverMsg
              ? serverMsg
              : '提交失败，请稍后重试。',
        );
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功，请稍后重试。');
    }
  };

  const inputClass =
    'w-full px-4 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300';

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">留下你的线索</h3>
      <p className="mt-2 text-sm text-foreground-600">
        请尽量写清时间、地点与情境。我们更看重完整描述，而不是情绪化判断。
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-2 font-label">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="如何称呼你"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-2 font-label">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于回复你"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="block text-xs text-foreground-600 mb-2 font-label">
          事由
        </label>
        <select id="contact-topic" name="topic" required className={inputClass} defaultValue="">
          <option value="" disabled>
            请选择事由
          </option>
          <option value="现场投稿">现场投稿 / 爆料</option>
          <option value="内容纠错">内容纠错与补充</option>
          <option value="合作咨询">内容合作咨询</option>
          <option value="隐私相关">隐私相关请求</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-2 font-label">
          详细描述
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你看到的场景、时间与地点（最多 500 字）"
          className={`${inputClass} resize-none`}
        />
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-honeypot-field"
      />

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-6 inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 px-6 py-3 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
      >
        <i className="ri-send-plane-line text-base leading-none" aria-hidden="true" />
        {status === 'submitting' ? '提交中…' : '提交线索'}
      </button>

      {status === 'success' && (
        <p className="mt-4 flex items-start gap-2 text-sm text-secondary-700">
          <i className="ri-checkbox-circle-line text-lg leading-none mt-0.5" aria-hidden="true" />
          已收到，感谢你的分享。我们会尽快查看并回复。
        </p>
      )}
      {status === 'error' && formError && (
        <p className="mt-4 flex items-start gap-2 text-sm text-primary-700">
          <i className="ri-error-warning-line text-lg leading-none mt-0.5" aria-hidden="true" />
          {formError}
        </p>
      )}
    </form>
  );
}